import { 
  ref, 
  push, 
  set, 
  update, 
  remove, 
  onValue, 
  off, 
  query, 
  orderByChild, 
  startAt, 
  endAt, 
  get, 
  limitToLast 
} from 'firebase/database';
import { database } from './core';
import { WorkoutSessionBase, WorkoutSession, WorkoutSessionSearchParams } from '../../types/workout-session';
import logger from '../../utils/logger';
import { healthSyncService } from '../health/health-sync.service';
import { Capacitor } from '@capacitor/core';
import { updateData } from './api-wrapper.service';

/**
 * Returns the path to the workout sessions for a user
 * @param userId The user ID
 * @returns The path to the user's workout sessions
 */
const getWorkoutSessionsPath = (userId: string) => `workoutSessions/${userId}`;

/**
 * Validates a workout session object before saving to Firebase
 * @param sessionData The workout session data to validate
 * @throws Error if the session data is invalid
 */
const validateSession = (sessionData: WorkoutSessionBase): void => {
  if (!sessionData.routineKey) {
    throw new Error('Workout session must have a routineKey');
  }
  
  if (!sessionData.sets || !Array.isArray(sessionData.sets) || sessionData.sets.length === 0) {
    throw new Error('Workout session must have at least one set');
  }
  
  sessionData.sets.forEach((set, setIndex) => {
    if (!set.title) {
      throw new Error(`Set at index ${setIndex} must have a title`);
    }
    
    if (!set.exercises || !Array.isArray(set.exercises) || set.exercises.length === 0) {
      throw new Error(`Set at index ${setIndex} must have at least one exercise`);
    }
    
    set.exercises.forEach((exercise, exerciseIndex) => {
      if (!exercise.name) {
        throw new Error(`Exercise at index ${exerciseIndex} in set ${setIndex} must have a name`);
      }
    });
  });
};

/**
 * Creates a new workout session in the database
 * @param userId The ID of the user who owns the session
 * @param sessionData The session data to save
 * @returns The key of the newly created session
 */
export const createWorkoutSession = async (
  userId: string,
  sessionData: WorkoutSessionBase
): Promise<string> => {
  validateSession(sessionData);
  
  try {
    const sessionsRef = ref(database, getWorkoutSessionsPath(userId));
    const newSessionRef = push(sessionsRef);
    
    if (!newSessionRef.key) {
      throw new Error('Failed to generate session key');
    }
    
    const currentTimestamp = Date.now();
    
    await set(newSessionRef, {
      ...sessionData,
      dateCreated: currentTimestamp,
      dateUpdated: currentTimestamp,
      author: userId
    });

    // Sync to Apple Health (iOS only, if enabled)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const isHealthEnabled = await healthSyncService.isProviderEnabled();
        if (isHealthEnabled) {
          logger.info(`Syncing workout session ${newSessionRef.key} to Apple Health`);

          // Build workout session object for sync
          const workoutForSync = {
            sessionKey: newSessionRef.key,
            dateCreated: currentTimestamp,
            duration: sessionData.duration,
          };

          const syncSuccess = await healthSyncService.syncWorkout(workoutForSync);

          if (syncSuccess) {
            // Update workout session with health sync metadata (matches original app format)
            const providerId = healthSyncService.getCurrentProviderId();
            const healthMetadata = healthSyncService.makeHealthProvidersMetadata(
              {},
              { [providerId]: true }
            );

            await updateData(`${getWorkoutSessionsPath(userId)}/${newSessionRef.key}`, healthMetadata);
            logger.info(`Workout session ${newSessionRef.key} synced to Apple Health successfully`);
          } else {
            logger.warn(`Failed to sync workout session ${newSessionRef.key} to Apple Health`);
          }
        }
      } catch (healthError) {
        // Log but don't fail the workout session creation if health sync fails
        logger.error('Error syncing workout session to Apple Health:', healthError);
      }
    }

    return newSessionRef.key;
  } catch (error) {
    logger.error('Error creating workout session:', error);
    throw error;
  }
};

/**
 * Updates an existing workout session in the database
 * @param userId The ID of the user who owns the session
 * @param sessionKey The key of the session to update
 * @param newData The new data to apply to the session
 */
export const updateWorkoutSession = async (
  userId: string,
  sessionKey: string,
  newData: Partial<WorkoutSessionBase>
): Promise<void> => {
  try {
    const sessionRef = ref(database, `${getWorkoutSessionsPath(userId)}/${sessionKey}`);
    
    // Update the dateUpdated timestamp
    const updates = {
      ...newData,
      dateUpdated: Date.now()
    };
    
    await update(sessionRef, updates);
  } catch (error) {
    logger.error('Error updating workout session:', error);
    throw error;
  }
};

/**
 * Deletes a workout session from the database
 * @param userId The ID of the user who owns the session
 * @param sessionKey The key of the session to delete
 */
export const deleteWorkoutSession = async (
  userId: string,
  sessionKey: string
): Promise<void> => {
  try {
    const sessionRef = ref(database, `${getWorkoutSessionsPath(userId)}/${sessionKey}`);
    await remove(sessionRef);
  } catch (error) {
    logger.error('Error deleting workout session:', error);
    throw error;
  }
};

/**
 * Gets a workout session by its key
 * @param userId The ID of the user who owns the session
 * @param sessionKey The key of the session to retrieve
 * @returns The workout session, or null if not found
 */
export const getWorkoutSession = async (
  userId: string,
  sessionKey: string
): Promise<WorkoutSession | null> => {
  try {
    const sessionRef = ref(database, `${getWorkoutSessionsPath(userId)}/${sessionKey}`);
    const snapshot = await get(sessionRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const sessionData = snapshot.val();
    return {
      ...sessionData,
      sessionKey
    };
  } catch (error) {
    logger.error('Error getting workout session:', error);
    throw error;
  }
};

/**
 * Gets all workout sessions for a user
 * @param userId The ID of the user whose sessions to retrieve
 * @param limit Optional limit on the number of sessions to retrieve
 * @returns An array of workout sessions
 */
export const getUserWorkoutSessions = async (
  userId: string,
  limit?: number
): Promise<WorkoutSession[]> => {
  try {
    const sessionsRef = ref(database, getWorkoutSessionsPath(userId));

    let snapshot;
    if (limit) {
      const sessionsQuery = query(sessionsRef, orderByChild('dateCreated'), limitToLast(limit));
      snapshot = await get(sessionsQuery);
    } else {
      snapshot = await get(sessionsRef);
    }
    
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const sessions: WorkoutSession[] = [];
    snapshot.forEach((childSnapshot) => {
      const sessionKey = childSnapshot.key;
      if (sessionKey) {
        const sessionData = childSnapshot.val();
        sessions.push({
          ...sessionData,
          sessionKey
        });
      }
    });
    
    return sessions;
  } catch (error) {
    logger.error('Error getting user workout sessions:', error);
    throw error;
  }
};

/**
 * Searches workout sessions based on search parameters
 * @param userId The ID of the user whose sessions to search
 * @param searchParams Parameters to filter the search
 * @returns An array of matching workout sessions
 */
export const searchWorkoutSessions = async (
  userId: string,
  searchParams: WorkoutSessionSearchParams
): Promise<WorkoutSession[]> => {
  try {
    const sessionsRef = ref(database, getWorkoutSessionsPath(userId));

    let snapshot;
    if (searchParams.orderByProp) {
      let queryConstraints = [orderByChild(searchParams.orderByProp)];

      if (searchParams.startDate && searchParams.orderByProp === 'dateCreated') {
        queryConstraints.push(startAt(searchParams.startDate));
      }

      if (searchParams.endDate && searchParams.orderByProp === 'dateCreated') {
        queryConstraints.push(endAt(searchParams.endDate));
      }

      const sessionsQuery = query(sessionsRef, ...queryConstraints);
      snapshot = await get(sessionsQuery);
    } else {
      snapshot = await get(sessionsRef);
    }
    
    
    if (!snapshot.exists()) {
      return [];
    }
    
    let sessions: WorkoutSession[] = [];
    snapshot.forEach((childSnapshot) => {
      const sessionKey = childSnapshot.key;
      if (sessionKey) {
        const sessionData = childSnapshot.val();
        sessions.push({
          ...sessionData,
          sessionKey
        });
      }
    });
    
    // Client-side filtering for complex conditions
    if (searchParams.keyword) {
      const keyword = searchParams.keyword.toLowerCase();
      sessions = sessions.filter(session => {
        // Check if keyword appears in notes
        if (session.notes && session.notes.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // Check if keyword appears in tags
        if (session.tags && session.tags.some(tag => tag.toLowerCase().includes(keyword))) {
          return true;
        }
        
        // Check if keyword appears in exercise names
        return session.sets.some(set =>
          set.exercises.some(exercise =>
            exercise.name.toLowerCase().includes(keyword)
          )
        );
      });
    }
    
    if (searchParams.tag) {
      sessions = sessions.filter(session =>
        session.tags && session.tags.includes(searchParams.tag as string)
      );
    }
    
    // Sort by specified property and direction
    if (searchParams.orderByProp) {
      sessions.sort((a, b) => {
        const aValue = a[searchParams.orderByProp as keyof WorkoutSession] as number;
        const bValue = b[searchParams.orderByProp as keyof WorkoutSession] as number;
        
        if (searchParams.orderDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }
    
    return sessions;
  } catch (error) {
    logger.error('Error searching workout sessions:', error);
    throw error;
  }
};

/**
 * Subscribes to changes on a workout session
 * @param userId The ID of the user who owns the session
 * @param sessionKey The key of the session to watch
 * @param callback Function to call when the session changes
 * @returns Function to unsubscribe
 */
export const subscribeToWorkoutSession = (
  userId: string,
  sessionKey: string,
  callback: (session: WorkoutSession | null) => void
): () => void => {
  const sessionRef = ref(database, `${getWorkoutSessionsPath(userId)}/${sessionKey}`);
  
  const onDataChange = (snapshot: any) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    
    const sessionData = snapshot.val();
    callback({
      ...sessionData,
      sessionKey
    });
  };
  
  onValue(sessionRef, onDataChange);
  
  // Return unsubscribe function
  return () => off(sessionRef, 'value', onDataChange);
};

/**
 * Subscribes to all workout sessions for a user
 * @param userId The ID of the user whose sessions to watch
 * @param callback Function to call when sessions change
 * @returns Function to unsubscribe
 */
export const subscribeToUserWorkoutSessions = (
  userId: string,
  callback: (sessions: WorkoutSession[]) => void
): () => void => {
  const sessionsRef = ref(database, getWorkoutSessionsPath(userId));
  
  const onDataChange = (snapshot: any) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }
    
    const sessions: WorkoutSession[] = [];
    snapshot.forEach((childSnapshot: any) => {
      const sessionKey = childSnapshot.key;
      if (sessionKey) {
        const sessionData = childSnapshot.val();
        sessions.push({
          ...sessionData,
          sessionKey
        });
      }
    });
    
    callback(sessions);
  };
  
  onValue(sessionsRef, onDataChange);
  
  // Return unsubscribe function
  return () => off(sessionsRef, 'value', onDataChange);
};